import React, { useMemo, useState } from 'react';
import CPPUtils from 'services/CPPUtils';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import RefillApproval from 'components/Patient/CPP/CPPTimeline/CPPTimelinePrescriptionRefill/RefillApproval';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import ConfirmationIcon from 'components/Utils/NewIcon/ConfirmationIcon';

const ACTIVE_TAB = 'prescription';

type CPPTimelinePrescriptionRefillProps = {
  refill: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
};

const CPPTimelinePrescriptionRefill = ({
  refill,
  patient,
  ...props
}: CPPTimelinePrescriptionRefillProps) => {
  const dispatch = useAppDispatch();
  const [deleting, setDeleting] = useState(false);
  const [refillModal, setRefillModal] = useState(false);

  async function deleteRefill() {
    setDeleting(true);
    await dispatch(
      api.HealthRecords.removePrescriptionRefill({ id: refill.id, patientId: refill.patient_id }),
      { errorTitle: 'Delete Refill Request' },
    );
    setDeleting(false);
  }

  function onClick() {
    setRefillModal(true);
  }

  const title = useMemo(() => getTitle(refill), [refill]);

  const isPending = !refill.status || refill.status === 'pending';

  return (
    <>
      {refillModal && (
        <DefaultModal
          className="modal refill-approval-modal"
          wrap={true}
          padding={true}
          title="Prescription Refill"
          content={
            <RefillApproval
              refill={refill}
              patient={patient}
              setActiveCPPTab={props.setActiveCPPTab}
              onClose={() => setRefillModal(false)}
            />
          }
          isOpen={refillModal}
          onRequestClose={() => setRefillModal(false)}
        />
      )}
      <DefaultTooltip title={refill.notes}>
        <div
          className="item-wrapper item-task clickable timeline-prescription-refill"
          onClick={onClick}
        >
          <>
            <HoverIcons
              //@ts-ignore
              refill={refill}
              onRemove={deleteRefill}
              isPending={isPending}
              deleting={deleting}
            />
            <div className="top-bar">
              <Icon src="prescription" />
              <span className="small-title">Pt. Refill Request</span>
            </div>
            <div className="item-content">
              <span>{title}</span>
              {!isPending && !!refill.physician_note && (
                <>
                  <br />
                  <br />
                  <span>
                    Practitioner Note:
                    <br />
                    {refill.physician_note}
                  </span>
                </>
              )}
              {isPending && !!refill.note && (
                <>
                  <br />
                  <br />
                  <span>
                    Patient Note:
                    <br />
                    {refill.note}
                  </span>
                </>
              )}
            </div>
          </>
        </div>
      </DefaultTooltip>
    </>
  );
};

const REFILL_STYLE: React.CSSProperties = { position: 'relative', top: '5px', right: '5px' };

const HoverIcons = React.memo(
  ({
    onRemove,
    isPending,
    deleting,
  }: {
    onRemove: () => void;
    isPending: boolean;
    deleting: boolean;
  }) => {
    return (
      <>
        <div className="hover-icons">
          {isPending && (
            <ConfirmationIcon
              src="trash"
              alt="Remove"
              height="20px"
              width="20px"
              //@ts-ignore
              style={REFILL_STYLE}
              className="edit"
              confirmText="This will delete the refill request. Confirm?"
              loading={deleting}
              onClick={onRemove}
            />
          )}
        </div>
      </>
    );
  },
);

function getTitle(refill: any) {
  return (refill.medications || []).map((medication: any) => medication.name).join('; ');
}

export default React.memo(CPPTimelinePrescriptionRefill);
